import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, BookOpen, Video, FileText, Users, Lightbulb, Target } from "lucide-react";
import { useEffect } from "react";

// SEO Component for Resources
const ResourcesSEO = () => {
  useEffect(() => {
    document.title = 'User Research Resources & Learning Materials | YakStak';
    
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Curated collection of the best user research resources, books, courses, and tools. Master user interviews, product discovery, and customer development with expert recommendations.');
    }
    
    // Add resources page structured data
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "User Research Resources",
      "description": "Curated resources to help you master user research, product discovery, and customer development",
      "url": "https://yakstak.app/resources",
      "publisher": {
        "@type": "Organization",
        "name": "YakStak",
        "url": "https://yakstak.app"
      },
      "hasPart": [
        {
          "@type": "ItemList",
          "name": "User Research Methods",
          "description": "Essential resources for conducting effective user research"
        },
        {
          "@type": "ItemList", 
          "name": "Product Discovery",
          "description": "Tools and techniques for discovering what users really need"
        },
        {
          "@type": "ItemList",
          "name": "Customer Development", 
          "description": "Best practices for validating ideas through customer feedback"
        }
      ]
    };
    
    let script = document.getElementById('resources-structured-data');
    if (script) {
      script.textContent = JSON.stringify(structuredData);
    } else {
      script = document.createElement('script');
      script.id = 'resources-structured-data';
      (script as HTMLScriptElement).type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
    
    return () => {
      const script = document.getElementById('resources-structured-data');
      if (script) {
        script.remove();
      }
    };
  }, []);
  
  return null;
};

const Resources = () => {
  const navigate = useNavigate();

  const resources = [
    {
      category: "User Research Methods",
      icon: <Users className="w-5 h-5" />,
      items: [
        {
          title: "The Mom Test by Rob Fitzpatrick",
          description: "Learn how to talk to customers and validate your business ideas through effective conversations.",
          type: "Book",
          link: "https://www.momtestbook.com/",
          free: false
        },
        {
          title: "Jobs-to-be-Done Framework Guide",
          description: "Comprehensive guide to understanding what customers are really hiring your product to do.",
          type: "Article",
          link: "https://hbr.org/2016/09/know-your-customers-jobs-to-be-done",
          free: true
        },
        {
          title: "User Interview Techniques",
          description: "Best practices for conducting effective user interviews that reveal true insights.",
          type: "Video Course",
          link: "https://www.nngroup.com/courses/interviewing-users/",
          free: false
        }
      ]
    },
    {
      category: "Product Discovery",
      icon: <Lightbulb className="w-5 h-5" />,
      items: [
        {
          title: "Continuous Discovery Habits by Teresa Torres",
          description: "Build a sustainable practice of discovering opportunities and solutions that work for your customers.",
          type: "Book", 
          link: "https://www.producttalk.org/book/",
          free: false
        },
        {
          title: "Product Discovery Canvas",
          description: "Free template to map out your discovery process and track key insights.",
          type: "Template",
          link: "https://miro.com/templates/product-discovery-canvas/",
          free: true
        },
        {
          title: "Building Products Users Love",
          description: "Video series on turning user insights into product decisions that drive growth.",
          type: "Video Series",
          link: "https://amplitude.com/blog/building-products-users-love",
          free: true
        }
      ]
    },
    {
      category: "Customer Development",
      icon: <Target className="w-5 h-5" />,
      items: [
        {
          title: "The Lean Startup by Eric Ries",
          description: "The foundational book on building products through validated learning and customer feedback.",
          type: "Book",
          link: "https://theleanstartup.com/",
          free: false
        },
        {
          title: "Talking to Humans by Giff Constable",
          description: "A practical guide to customer development and validation through effective customer conversations.",
          type: "Book",
          link: "https://www.goodreads.com/book/show/23314559-talking-to-humans",
          free: false
        },
        {
          title: "Customer Development Checklist",
          description: "Step-by-step checklist for systematic customer development interviews.",
          type: "Checklist",
          link: "https://steveblank.com/tools-and-blogs-for-entrepreneurs/",
          free: true
        },
        {
          title: "From Idea to Product-Market Fit",
          description: "Practical course on validating ideas and finding product-market fit through customer insights.",
          type: "Online Course",
          link: "https://www.coursera.org/learn/entrepreneurship-development",
          free: false
        }
      ]
    }
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "Book": return <BookOpen className="w-4 h-4" />;
      case "Video Course":
      case "Video Series": return <Video className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-background">
      <ResourcesSEO />
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" role="banner">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
            <div className="flex items-center gap-2">
              <img className="w-8 h-8" src="/yak-stak-logo.png" alt="YakStak Logo" />
              <span className="text-xl font-bold text-foreground">YakStak</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12" role="main">
        <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-foreground mb-4" itemProp="headline">
              Recommended Resources
            </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Curated resources to help you master user research, product discovery, and customer development
          </p>
        </div>

        {/* Recommended Tools Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <Target className="w-5 h-5" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground">
              Recommended Tools
            </h2>
          </div>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Card className="h-full">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    <Badge variant="secondary">Tool</Badge>
                  </div>
                  <ExternalLink className="w-4 h-4 text-muted-foreground" />
                </div>
                <CardTitle className="text-lg">Buildpad</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <CardDescription className="mb-4">
                  AI-powered platform that helps you research and build products that people actually want. Validate ideas, understand markets, and make data-driven product decisions.
                </CardDescription>
                <Button 
                  asChild 
                  className="w-full"
                  variant="default"
                >
                  <a 
                    href="http://buildpad.io/?via=kieran" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2"
                  >
                    View Tool
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        <div className="space-y-12">
          {resources.map((category, categoryIndex) => (
            <section key={categoryIndex}>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  {category.icon}
                </div>
                <h2 className="text-2xl font-semibold text-foreground">
                  {category.category}
                </h2>
              </div>
              
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {category.items.map((resource, resourceIndex) => (
                  <Card key={resourceIndex} className="h-full">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          {getTypeIcon(resource.type)}
                          <Badge variant={resource.free ? "secondary" : "outline"}>
                            {resource.free ? "Free" : resource.type}
                          </Badge>
                        </div>
                        <ExternalLink className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <CardTitle className="text-lg">{resource.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <CardDescription className="mb-4">
                        {resource.description}
                      </CardDescription>
                      <Button 
                        asChild 
                        className="w-full"
                        variant={resource.free ? "default" : "outline"}
                      >
                        <a 
                          href={resource.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2"
                        >
                          View Resource
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* CTA Section */}
        <section className="mt-16 text-center">
          <Card className="bg-gradient-subtle border-primary/20">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Ready to put these insights into practice?
              </h3>
              <p className="text-muted-foreground mb-6">
                YakStak helps you organize and analyze your user research insights more effectively
              </p>
              <Button onClick={() => navigate("/#cta")}>
                Join the Waitlist
              </Button>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
};

export default Resources;